<script>
import { defineAsyncComponent } from 'vue';
import { mapGetters } from 'vuex';
import { toast } from 'dashboard/composables/useToast';
import { DirectUpload } from 'activestorage';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import {
  getMaxUploadSizeByChannel,
  getAllowedFileTypesByChannel,
} from '@chatwoot/utils';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { AUDIO_FORMATS } from 'shared/constants/messages';
import FileUpload from 'vue-upload-component';
import AudioRecorder from 'dashboard/components/widgets/WootWriter/AudioRecorder.vue';

import NextDialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ScheduledMessagesList from './ScheduledMessagesList.vue';
import WindowWarningBanner from './WindowWarningBanner.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';

const EmojiInput = defineAsyncComponent(
  () => import('shared/components/emoji/EmojiInput.vue')
);

export default {
  components: {
    NextDialog,
    NextButton,
    ScheduledMessagesList,
    WindowWarningBanner,
    WootMessageEditor,
    AttachmentPreview,
    EmojiInput,
    FileUpload,
    AudioRecorder,
  },
  props: {
    conversationId: {
      type: Number,
      required: true,
    },
    inboxId: {
      type: Number,
      required: true,
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    currentMessage: {
      type: String,
      default: '',
    },
    canReply: {
      type: Boolean,
      default: true,
    },
    contactId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close'],
  data() {
    const defaultDate = new Date();
    defaultDate.setMinutes(defaultDate.getMinutes() + 60);

    return {
      messageContent: this.currentMessage || '',
      scheduledDate: `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, '0')}-${String(defaultDate.getDate()).padStart(2, '0')}`,
      scheduledTime: `${String(defaultDate.getHours()).padStart(2, '0')}:${String(defaultDate.getMinutes()).padStart(2, '0')}`,
      isSubmitting: false,
      showEmojiPicker: false,
      attachedFiles: [],
      validationErrors: { date: '', time: '', message: '' },
      editingMessageId: null,
      isRecordingAudio: false,
      recordingAudioDurationText: '',
      recordingAudioState: '',
      hasRecordedAudio: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
    }),
    channelType() {
      return this.inbox?.channel_type || '';
    },
    inboxName() {
      return this.inbox?.name || '';
    },
    today() {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },
    scheduledDateTime() {
      if (!this.scheduledDate || !this.scheduledTime) return null;
      return new Date(`${this.scheduledDate}T${this.scheduledTime}`);
    },
    isScheduleBlocked() {
      return this.$refs.warningBanner?.isBlockedByWindow || false;
    },
    hasAttachments() {
      return this.attachedFiles.length > 0;
    },
    isFormValid() {
      const hasContent =
        this.messageContent.trim().length > 0 || this.hasAttachments;
      if (!hasContent) return false;
      if (!this.scheduledDate || !this.scheduledTime) return false;
      if (!this.scheduledDateTime || this.scheduledDateTime <= new Date())
        return false;
      if (this.isScheduleBlocked) return false;
      return true;
    },
    showAudioRecorderEditor() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    // Inbox type checks
    isWhatsAppChannel() {
      return (
        this.channelType === INBOX_TYPES.WHATSAPP ||
        (this.channelType === INBOX_TYPES.TWILIO &&
          this.inbox?.medium === 'whatsapp')
      );
    },
    isInstagramChannel() {
      return this.channelType === INBOX_TYPES.INSTAGRAM;
    },
    isFacebookChannel() {
      return this.channelType === INBOX_TYPES.FB;
    },
    isEmailChannel() {
      return this.channelType === INBOX_TYPES.EMAIL;
    },
    isWebWidgetChannel() {
      return this.channelType === INBOX_TYPES.WEB;
    },
    isAPIChannel() {
      return this.channelType === INBOX_TYPES.API;
    },
    isTelegramChannel() {
      return this.channelType === INBOX_TYPES.TELEGRAM;
    },
    isSmsChannel() {
      return (
        this.channelType === INBOX_TYPES.SMS ||
        (this.channelType === INBOX_TYPES.TWILIO &&
          this.inbox?.medium === 'sms')
      );
    },
    isDinastiaWhatsAppChannel() {
      return this.channelType === INBOX_TYPES.DINASTIA_WHATSAPP;
    },
    showFileUpload() {
      return (
        this.isWebWidgetChannel ||
        this.isFacebookChannel ||
        this.isWhatsAppChannel ||
        this.isDinastiaWhatsAppChannel ||
        this.isAPIChannel ||
        this.isEmailChannel ||
        this.isTelegramChannel ||
        this.isInstagramChannel
      );
    },
    showAudioRecorder() {
      return (
        this.isWebWidgetChannel ||
        this.isWhatsAppChannel ||
        this.isDinastiaWhatsAppChannel ||
        this.isAPIChannel ||
        this.isTelegramChannel ||
        this.isFacebookChannel
      );
    },
    audioRecordFormat() {
      if (
        this.isWhatsAppChannel ||
        this.isDinastiaWhatsAppChannel ||
        this.isTelegramChannel ||
        this.isAPIChannel
      ) {
        return AUDIO_FORMATS.MP3;
      }
      return AUDIO_FORMATS.WAV;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    allowedFileTypes() {
      return getAllowedFileTypesByChannel({
        channelType: this.channelType,
        medium: this.inbox?.medium,
      });
    },
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig?.maximumFileUploadSize
      );
    },
    isEditing() {
      return this.editingMessageId !== null;
    },
    channelIcon() {
      const iconMap = {
        [INBOX_TYPES.WHATSAPP]: 'i-lucide-message-circle',
        [INBOX_TYPES.TWILIO]: 'i-lucide-message-circle',
        [INBOX_TYPES.FB]: 'i-lucide-facebook',
        [INBOX_TYPES.INSTAGRAM]: 'i-lucide-instagram',
        [INBOX_TYPES.EMAIL]: 'i-lucide-mail',
        [INBOX_TYPES.WEB]: 'i-lucide-globe',
        [INBOX_TYPES.API]: 'i-lucide-code',
        [INBOX_TYPES.TELEGRAM]: 'i-lucide-send',
        [INBOX_TYPES.SMS]: 'i-lucide-smartphone',
        [INBOX_TYPES.LINE]: 'i-lucide-message-square',
        [INBOX_TYPES.DINASTIA_WHATSAPP]: 'i-lucide-message-circle',
      };
      return iconMap[this.channelType] || 'i-lucide-inbox';
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.dialogRef?.open();
    });
  },
  methods: {
    closeDialog() {
      this.$emit('close');
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      this.showEmojiPicker = false;
    },
    insertEmoji(emoji) {
      this.messageContent += emoji;
    },
    // Audio recording methods
    toggleAudioRecorder() {
      if (this.isRecordingAudio) {
        // Cancel recording — discard without saving
        this.resetAudioRecorder();
      } else {
        this.isRecordingAudio = true;
      }
    },
    toggleAudioRecorderPlayPause() {
      if (!this.$refs.audioRecorderInput) return;
      if (!this.recordingAudioState) {
        // First click = stop recording (triggers record-end -> finishRecord)
        this.$refs.audioRecorderInput.stopRecording();
      } else {
        // After stopped, toggle play/pause preview
        this.$refs.audioRecorderInput.playPause();
      }
    },
    resetAudioRecorder() {
      this.isRecordingAudio = false;
      this.recordingAudioState = '';
      this.recordingAudioDurationText = '';
      this.hasRecordedAudio = false;
      // Remove any recorded audio from attachments
      this.attachedFiles = this.attachedFiles.filter(f => !f?.isRecordedAudio);
    },
    onRecordProgressChanged(duration) {
      this.recordingAudioDurationText = duration;
    },
    onFinishRecorder(file) {
      this.recordingAudioState = 'stopped';
      this.hasRecordedAudio = true;
      const audioFile = file;
      audioFile.isRecordedAudio = true;
      this.onFileUpload(audioFile);
      // Hide recorder after file is attached
      this.isRecordingAudio = false;
    },
    // File upload methods
    maxSizeFor(mime) {
      if (!this.channelType || this.channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }
      const channelLimit = getMaxUploadSizeByChannel({
        channelType: this.channelType,
        medium: this.inbox?.medium,
        mime,
      });
      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }
      return Math.min(channelLimit, this.installationLimit);
    },
    onFileUpload(file) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        toast.error(
          this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
            MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
          })
        );
        return;
      }

      if (this.globalConfig?.directUploadsEnabled) {
        this.directUpload(file);
      } else {
        this.attachFile({ file });
      }
    },
    directUpload(file) {
      const upload = new DirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${this.conversationId}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          toast.error(error);
        } else {
          this.attachFile({ file, blob });
        }
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.conversationId,
          resource: blob || file,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(attachments) {
      this.attachedFiles = attachments;
    },
    validateForm() {
      this.validationErrors = { date: '', time: '', message: '' };
      let valid = true;

      const hasContent =
        this.messageContent.trim().length > 0 || this.hasAttachments;
      if (!hasContent) {
        this.validationErrors.message = this.$t(
          'SCHEDULED_MESSAGES.DIALOG.VALIDATION.MESSAGE_REQUIRED'
        );
        valid = false;
      }

      if (!this.scheduledDate) {
        this.validationErrors.date = this.$t(
          'SCHEDULED_MESSAGES.DIALOG.VALIDATION.DATE_REQUIRED'
        );
        valid = false;
      }

      if (!this.scheduledTime) {
        this.validationErrors.time = this.$t(
          'SCHEDULED_MESSAGES.DIALOG.VALIDATION.TIME_REQUIRED'
        );
        valid = false;
      }

      if (
        this.scheduledDate &&
        this.scheduledTime &&
        this.scheduledDateTime <= new Date()
      ) {
        this.validationErrors.date = this.$t(
          'SCHEDULED_MESSAGES.DIALOG.VALIDATION.PAST_DATE'
        );
        valid = false;
      }

      return valid;
    },
    editScheduledMessage(msg) {
      this.editingMessageId = msg.id;
      this.messageContent = msg.content || '';
      if (msg.scheduled_at) {
        const dt = new Date(msg.scheduled_at);
        this.scheduledDate = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
        this.scheduledTime = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
      }
      this.attachedFiles = [];
    },
    cancelEdit() {
      this.editingMessageId = null;
      this.messageContent = '';
      this.attachedFiles = [];
      const defaultDate = new Date();
      defaultDate.setMinutes(defaultDate.getMinutes() + 60);
      this.scheduledDate = `${defaultDate.getFullYear()}-${String(defaultDate.getMonth() + 1).padStart(2, '0')}-${String(defaultDate.getDate()).padStart(2, '0')}`;
      this.scheduledTime = `${String(defaultDate.getHours()).padStart(2, '0')}:${String(defaultDate.getMinutes()).padStart(2, '0')}`;
    },
    buildPayload() {
      const payload = {
        scheduled_message: {
          conversation_id: this.conversationId,
          inbox_id: this.inboxId,
          content: this.messageContent.trim(),
          scheduled_at: this.scheduledDateTime.toISOString(),
        },
      };
      if (this.hasAttachments) {
        const fileIds = this.attachedFiles
          .map(f => f.blobSignedId)
          .filter(Boolean);
        if (fileIds.length) {
          payload.scheduled_message.additional_attributes = {
            files: fileIds,
          };
        }
      }
      return payload;
    },
    async scheduleMessage() {
      if (!this.validateForm()) return;

      this.isSubmitting = true;
      try {
        if (this.isEditing) {
          await this.$store.dispatch('scheduledMessages/update', {
            id: this.editingMessageId,
            ...this.buildPayload(),
          });
          toast.success(this.$t('SCHEDULED_MESSAGES.ALERTS.UPDATED'));
          this.editingMessageId = null;
        } else {
          await this.$store.dispatch(
            'scheduledMessages/create',
            this.buildPayload()
          );
          const fmtDate = this.scheduledDateTime.toLocaleDateString('pt-BR');
          const fmtTime = this.scheduledTime;
          toast.success(
            this.$t('SCHEDULED_MESSAGES.ALERTS.CREATED', {
              date: fmtDate,
              time: fmtTime,
            })
          );
        }

        // Refresh the list
        this.$store.dispatch('scheduledMessages/getByContact', this.contactId);

        // Reset form
        this.messageContent = '';
        this.attachedFiles = [];
        this.scheduledDate = '';
        this.scheduledTime = '';
      } catch {
        toast.error(this.$t('SCHEDULED_MESSAGES.ALERTS.CREATE_ERROR'));
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <NextDialog
    ref="dialogRef"
    :title="$t('SCHEDULED_MESSAGES.DIALOG.TITLE')"
    width="3xl"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
    @close="closeDialog"
  >
    <div class="flex flex-col gap-4 md:flex-row md:gap-6">
      <!-- Left: Schedule Form -->
      <div class="flex flex-col flex-1 gap-4 min-w-0">
        <!-- Inbox Info (read-only) -->
        <div
          class="flex items-center gap-2 px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-11"
        >
          <span :class="channelIcon" class="size-4 shrink-0" />
          <span class="truncate">{{ inboxName }}</span>
          <span class="text-n-slate-9">|</span>
          <span class="text-n-slate-10">
            {{ $t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONVERSATION') }} #{{
              conversationId
            }}
          </span>
        </div>

        <!-- Window Warning -->
        <WindowWarningBanner
          ref="warningBanner"
          :inbox="inbox"
          :can-reply="canReply"
          :scheduled-at="scheduledDateTime"
        />

        <!-- Message Editor -->
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_LABEL') }}
          </label>
          <div
            class="border rounded-lg bg-n-solid-2 border-n-weak overflow-hidden"
            :class="{ 'opacity-50 pointer-events-none': isScheduleBlocked }"
          >
            <!-- Audio Recorder replaces editor when recording -->
            <div v-if="showAudioRecorderEditor" class="px-3 py-3">
              <AudioRecorder
                ref="audioRecorderInput"
                :audio-record-format="audioRecordFormat"
                @recorder-progress-changed="onRecordProgressChanged"
                @finish-record="onFinishRecorder"
                @play="recordingAudioState = 'playing'"
                @pause="recordingAudioState = 'paused'"
              />
            </div>
            <WootMessageEditor
              v-else
              v-model="messageContent"
              editor-id="scheduled-message-editor"
              class="px-3 py-2 scheduled-editor"
              :is-private="false"
              :placeholder="$t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_PLACEHOLDER')"
              :min-height="4"
              @focus="hideEmojiPicker"
            />

            <!-- Toolbar -->
            <div
              class="flex items-center gap-0.5 px-2 py-1.5 border-t border-n-weak"
            >
              <NextButton
                v-if="!showAudioRecorderEditor"
                v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
                icon="i-ph-smiley-sticker"
                slate
                faded
                xs
                @click="toggleEmojiPicker"
              />
              <FileUpload
                v-if="showFileUpload && !showAudioRecorderEditor"
                v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                input-id="scheduleAttachment"
                :size="4096 * 4096"
                :accept="allowedFileTypes"
                multiple
                :drop="false"
                :drop-directory="false"
                :data="{
                  direct_upload_url: '/rails/active_storage/direct_uploads',
                  direct_upload: true,
                }"
                @input-file="onFileUpload"
              >
                <NextButton
                  v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                  icon="i-ph-paperclip"
                  slate
                  faded
                  xs
                />
              </FileUpload>
              <NextButton
                v-if="showAudioRecorder"
                v-tooltip.top="
                  $t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')
                "
                :icon="
                  !isRecordingAudio
                    ? 'i-ph-microphone'
                    : 'i-ph-microphone-slash'
                "
                slate
                faded
                xs
                @click="toggleAudioRecorder"
              />
              <NextButton
                v-if="showAudioRecorderEditor"
                :icon="audioRecorderPlayStopIcon"
                slate
                faded
                xs
                :label="recordingAudioDurationText"
                @click="toggleAudioRecorderPlayPause"
              />
            </div>
          </div>

          <!-- Emoji Picker -->
          <EmojiInput
            v-if="showEmojiPicker"
            v-on-clickaway="hideEmojiPicker"
            class="!relative !left-0 !right-0"
            :on-click="insertEmoji"
          />

          <!-- Attachment Preview -->
          <AttachmentPreview
            v-if="hasAttachments"
            :attachments="attachedFiles"
            class="mt-1"
            @remove-attachment="removeAttachment"
          />

          <span v-if="validationErrors.message" class="text-xs text-n-ruby-11">
            {{ validationErrors.message }}
          </span>
        </div>

        <!-- Date & Time -->
        <div class="flex gap-3">
          <div class="flex flex-col flex-1 gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('SCHEDULED_MESSAGES.DIALOG.DATE_LABEL') }}
            </label>
            <input
              v-model="scheduledDate"
              type="date"
              :min="today"
              class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
              :disabled="isScheduleBlocked"
            />
            <span v-if="validationErrors.date" class="text-xs text-n-ruby-11">
              {{ validationErrors.date }}
            </span>
          </div>
          <div class="flex flex-col flex-1 gap-1.5">
            <label class="text-sm font-medium text-n-slate-12">
              {{ $t('SCHEDULED_MESSAGES.DIALOG.TIME_LABEL') }}
            </label>
            <input
              v-model="scheduledTime"
              type="time"
              class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
              :disabled="isScheduleBlocked"
            />
            <span v-if="validationErrors.time" class="text-xs text-n-ruby-11">
              {{ validationErrors.time }}
            </span>
          </div>
        </div>

        <!-- Onde a mensagem vai aparecer e onde acompanhar -->
        <p
          class="mb-0 flex items-start gap-1.5 rounded-lg bg-n-alpha-1 px-3 py-2 text-xs text-n-slate-11"
        >
          <span
            class="i-lucide-info mt-0.5 size-3.5 shrink-0 text-n-slate-10"
          />
          <span>{{ $t('SCHEDULED_MESSAGES.DIALOG.HINT') }}</span>
        </p>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-2">
          <NextButton
            v-if="isEditing"
            slate
            faded
            type="button"
            :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
            @click="cancelEdit"
          />
          <NextButton
            v-else
            slate
            faded
            type="button"
            :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
            @click="closeDialog"
          />
          <NextButton
            blue
            :label="
              isEditing
                ? $t('SCHEDULED_MESSAGES.ACTIONS.UPDATE')
                : $t('SCHEDULED_MESSAGES.DIALOG.SCHEDULE_ACTION')
            "
            :icon="isEditing ? 'i-lucide-check' : 'i-lucide-calendar-clock'"
            :disabled="!isFormValid || isSubmitting"
            :is-loading="isSubmitting"
            @click="scheduleMessage"
          />
        </div>
      </div>

      <!-- Right: History -->
      <div
        class="flex flex-col w-full md:w-[320px] md:border-l md:border-n-weak md:pl-6 border-t md:border-t-0 pt-4 md:pt-0"
      >
        <ScheduledMessagesList
          :contact-id="contactId"
          @edit="editScheduledMessage"
        />
      </div>
    </div>
  </NextDialog>
</template>

<style lang="scss" scoped>
.scheduled-editor {
  :deep(.ProseMirror) {
    min-height: 80px;
    max-height: 200px;
    overflow-y: auto;
  }
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}

input[type='date'],
input[type='time'] {
  :global(.dark) & {
    color-scheme: dark;
  }
}
</style>
